/**
 * ColorPicker Component
 *
 * A colorblind-friendly color picker with a 5x5 grid of accessible colors.
 * Uses HeroUI Popover for the dropdown and supports react-hook-form integration.
 *
 * @example
 * ```tsx
 * // Controlled mode
 * <ColorPicker value={color} onChange={setColor} />
 *
 * // With react-hook-form
 * <ColorPicker {...register('color')} />
 * ```
 */

import { Popover, PopoverTrigger, PopoverContent, Button, Tooltip } from '@heroui/react';
import { forwardRef, useCallback, useRef, useState } from 'react';

import { cn, ARTIFACT_COLORS, getArtifactColorByValue } from '@/lib/design-system';

const GRID_COLUMNS = 5;
const GRID_ROWS = 5;

interface ColorPickerProps {
  /** Current selected color (hex format) */
  value?: string;
  /** Callback when color changes */
  onChange?: (color: string) => void;
  /** Field name for react-hook-form */
  name?: string;
  /** Blur handler for react-hook-form */
  onBlur?: () => void;
  /** Disable the picker */
  disabled?: boolean;
  /** Size of the trigger button */
  size?: 'sm' | 'md' | 'lg';
  /** Additional class name */
  className?: string;
}

export const ColorPicker = forwardRef<HTMLButtonElement, ColorPickerProps>(
  function ColorPicker(
    { value, onChange, name, onBlur, disabled = false, size = 'md', className },
    ref
  ) {
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const gridRef = useRef<HTMLDivElement>(null);

    // Get the current color info for display
    const currentColor = value ? getArtifactColorByValue(value) : undefined;
    const displayValue = value || ARTIFACT_COLORS[0].value;
    const displayLabel = currentColor?.label || 'Select a color';

    // Size mappings for the trigger button
    const sizeClasses = {
      sm: 'h-6 w-6 min-w-6',
      md: 'h-8 w-8 min-w-8',
      lg: 'h-10 w-10 min-w-10',
    };

    // Size mappings for the swatches
    const swatchSizeClasses = {
      sm: 'h-5 w-5',
      md: 'h-6 w-6',
      lg: 'h-7 w-7',
    };

    const handleColorSelect = useCallback(
      (color: string) => {
        onChange?.(color);
        setIsOpen(false);
        onBlur?.();
      },
      [onChange, onBlur]
    );

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        const { key } = event;

        // If grid is not focused, ignore
        if (focusedIndex === -1 && !['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(key)) {
          return;
        }

        // Initialize focus if not set
        let newIndex = focusedIndex === -1 ? 0 : focusedIndex;

        switch (key) {
          case 'ArrowRight':
            event.preventDefault();
            newIndex = (newIndex + 1) % ARTIFACT_COLORS.length;
            break;
          case 'ArrowLeft':
            event.preventDefault();
            newIndex = (newIndex - 1 + ARTIFACT_COLORS.length) % ARTIFACT_COLORS.length;
            break;
          case 'ArrowDown':
            event.preventDefault();
            newIndex = (newIndex + GRID_COLUMNS) % ARTIFACT_COLORS.length;
            break;
          case 'ArrowUp':
            event.preventDefault();
            newIndex = (newIndex - GRID_COLUMNS + ARTIFACT_COLORS.length) % ARTIFACT_COLORS.length;
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            if (focusedIndex >= 0) {
              handleColorSelect(ARTIFACT_COLORS[focusedIndex].value);
            }
            return;
          case 'Escape':
            event.preventDefault();
            setIsOpen(false);
            return;
          case 'Home':
            event.preventDefault();
            newIndex = 0;
            break;
          case 'End':
            event.preventDefault();
            newIndex = ARTIFACT_COLORS.length - 1;
            break;
          default:
            return;
        }

        setFocusedIndex(newIndex);

        // Focus the swatch button
        const button = gridRef.current?.querySelector(
          `[data-index="${newIndex}"]`
        ) as HTMLButtonElement | null;
        button?.focus();
      },
      [focusedIndex, handleColorSelect]
    );

    const handleOpenChange = useCallback((open: boolean) => {
      setIsOpen(open);
      if (open) {
        // Find the currently selected color index, or default to 0
        const selectedIndex = value
          ? ARTIFACT_COLORS.findIndex(
              (c) => c.value.toLowerCase() === value.toLowerCase()
            )
          : 0;
        setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
      } else {
        setFocusedIndex(-1);
      }
    }, [value]);

    return (
      <Popover
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        placement="bottom"
        offset={8}
        shouldFlip={false}
      >
        <PopoverTrigger>
          <Button
            ref={ref}
            type="button"
            isIconOnly
            variant="bordered"
            isDisabled={disabled}
            aria-label={displayLabel}
            aria-haspopup="grid"
            aria-expanded={isOpen}
            className={cn(
              sizeClasses[size],
              'rounded-md border-2 border-default-200 p-0.5',
              'hover:border-default-400 focus:border-primary',
              'transition-colors duration-150',
              className
            )}
          >
            <span
              className="block h-full w-full rounded-sm"
              style={{ backgroundColor: displayValue }}
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="p-2">
          <div
            ref={gridRef}
            role="grid"
            aria-label="Color selection"
            className="grid gap-1"
            style={{
              gridTemplateColumns: `repeat(${GRID_COLUMNS}, 1fr)`,
              gridTemplateRows: `repeat(${GRID_ROWS}, 1fr)`,
            }}
            onKeyDown={handleKeyDown}
          >
            {ARTIFACT_COLORS.map((color, index) => {
              const isSelected =
                value?.toLowerCase() === color.value.toLowerCase();
              const isFocused = focusedIndex === index;
              const row = Math.floor(index / GRID_COLUMNS);
              const col = index % GRID_COLUMNS;

              return (
                <Tooltip
                  key={color.value}
                  content={color.name}
                  delay={300}
                  closeDelay={0}
                >
                  <button
                    type="button"
                    role="gridcell"
                    aria-label={color.label}
                    aria-selected={isSelected}
                    aria-rowindex={row + 1}
                    aria-colindex={col + 1}
                    data-index={index}
                    tabIndex={isFocused || (focusedIndex === -1 && index === 0) ? 0 : -1}
                    className={cn(
                      swatchSizeClasses[size],
                      'rounded-sm transition-all duration-100',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
                      'hover:scale-110 hover:shadow-md',
                      isSelected && 'ring-2 ring-primary ring-offset-1 scale-110'
                    )}
                    style={{ backgroundColor: color.value }}
                    onClick={() => handleColorSelect(color.value)}
                    onFocus={() => setFocusedIndex(index)}
                  />
                </Tooltip>
              );
            })}
          </div>

          {/* Color name display */}
          <div className="mt-2 border-t border-default-100 pt-2 text-center">
            <span className="text-xs text-default-500">
              {focusedIndex >= 0
                ? ARTIFACT_COLORS[focusedIndex].name
                : currentColor?.name || 'Select a color'}
            </span>
          </div>

          {/* Hidden input for form submission */}
          {name && (
            <input type="hidden" name={name} value={value || ''} />
          )}
        </PopoverContent>
      </Popover>
    );
  }
);
